import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import Layout from '../components/layout/Layout';
import Badge from '../components/common/Badge';
import Button from '../components/common/Button';
import Input from '../components/common/Input';
import PageHeader, { EmptyState, KpiTile, Spinner } from '../components/common/PageHeader';
import { NativeSelect } from '../components/common/FormField';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { useConfirm } from '../hooks/useConfirm';
import { queryKeys } from '../lib/queryClient';
import { api, errorMessage, triggerBlobDownload } from '../services/api';
import { PayrollRow, PayrollSheet } from '../types';
import { currentMonth, fmtCurrency, fmtDate, fmtMonth, monthBounds, PAYMENT_MODES, shiftMonth, todayISO, toNum } from '../utils/format';
import { compact, focusFirstError, hasErrors, v } from '../utils/validation';

interface Draft {
  include: boolean;
  salary: string;
  allowances: string;
  remarks: string;
}

type RowErrors = Partial<Record<'salary' | 'allowances' | 'remarks', string>>;

// Money as typed: digits with at most 2 decimals.
const moneyInput = (raw: string) => {
  const cleaned = raw.replace(/[^0-9.]/g, '');
  const [int, ...rest] = cleaned.split('.');
  return rest.length ? `${int.slice(0, 10)}.${rest.join('').slice(0, 2)}` : int.slice(0, 10);
};

export default function PayrollPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const { hasPermission } = useAuth();
  const { confirm, ConfirmDialog } = useConfirm();
  const canRun = hasPermission('payroll:run');

  // Salaries are normally paid for the month that just ended.
  const [month, setMonth] = useState(shiftMonth(currentMonth(), -1));
  const [paymentDate, setPaymentDate] = useState(todayISO());
  const [paymentMode, setPaymentMode] = useState('bank_transfer');
  const [drafts, setDrafts] = useState<Record<number, Draft>>({});
  const [rowErrors, setRowErrors] = useState<Record<number, RowErrors>>({});
  const [slipFor, setSlipFor] = useState<number | null>(null);
  const [formErrors, setFormErrors] = useState<{ paymentDate?: string; paymentMode?: string }>({});

  const { data: sheet, isLoading } = useQuery({
    queryKey: queryKeys.employees.payroll(month),
    queryFn: () => api.get<PayrollSheet>('/employees/payroll', { month }),
  });

  // Seed editable amounts from the suggested (prorated) values.
  useEffect(() => {
    if (!sheet) return;
    const next: Record<number, Draft> = {};
    for (const r of sheet.rows) {
      if (r.isPaid) continue;
      next[r.employee.id] = {
        include: r.employee.status === 'active' || !!r.employee.exitDate,
        salary: String(r.suggestedSalary),
        allowances: r.suggestedAllowances ? String(r.suggestedAllowances) : '',
        remarks: '',
      };
    }
    setDrafts(next);
    setRowErrors({});
  }, [sheet]);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.employees.payroll(month) });
    queryClient.invalidateQueries({ queryKey: queryKeys.expenses.all() });
    queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    queryClient.invalidateQueries({ queryKey: ['reports'] });
  };

  const runMutation = useMutation({
    mutationFn: (entries: { employeeId: number; salary: number; allowances: number; remarks?: string }[]) =>
      api.post('/employees/payroll/run', { month, paymentDate, paymentMode, entries }),
    onSuccess: invalidate,
  });
  const undoMutation = useMutation({
    mutationFn: (employeeId: number) => api.delete('/employees/payroll', { month, employeeId }),
    onSuccess: invalidate,
  });

  function patch(id: number, p: Partial<Draft>) {
    setDrafts((d) => ({ ...d, [id]: { ...d[id], ...p } }));
    setRowErrors((e) => {
      if (!e[id]) return e;
      const { [id]: _cleared, ...rest } = e;
      return rest;
    });
  }

  const rows = sheet?.rows ?? [];
  const pending = rows.filter((r) => !r.isPaid);
  const selected = pending.filter((r) => drafts[r.employee.id]?.include);
  const selectedTotal = selected.reduce(
    (s, r) => s + (toNum(drafts[r.employee.id].salary) ?? 0) + (toNum(drafts[r.employee.id].allowances) ?? 0),
    0,
  );

  async function run() {
    if (selected.length === 0) return showToast('Select at least one employee', 'warning');

    const { from: monthStart } = monthBounds(month);
    const form = compact({
      paymentDate: v.date(paymentDate, 'Payment date', { notFuture: true, min: monthStart, minLabel: `the payroll month (${fmtMonth(month)})` }),
      paymentMode: paymentMode ? undefined : 'Payment mode is required',
    });
    const rowsWithErrors: Record<number, RowErrors> = {};
    for (const r of selected) {
      const d = drafts[r.employee.id];
      const e = compact({
        salary: v.money(d.salary, 'Salary', { allowZero: true }),
        allowances: v.money(d.allowances, 'Allowances', { required: false, allowZero: true }),
        remarks: v.text(d.remarks, 'Remarks', { max: 255 }),
      });
      if (!e.salary && !e.allowances && (toNum(d.salary) ?? 0) + (toNum(d.allowances) ?? 0) <= 0) {
        e.salary = 'Enter a salary or allowance amount greater than 0';
      }
      if (hasErrors(e)) rowsWithErrors[r.employee.id] = e;
    }
    setFormErrors(form);
    setRowErrors(rowsWithErrors);
    if (hasErrors(form) || hasErrors(rowsWithErrors)) {
      focusFirstError();
      return showToast('Please fix the highlighted fields', 'error');
    }

    const entries = selected.map((r) => ({
      employeeId: r.employee.id,
      salary: toNum(drafts[r.employee.id].salary) ?? 0,
      allowances: toNum(drafts[r.employee.id].allowances) ?? 0,
      remarks: drafts[r.employee.id].remarks.trim() || undefined,
    }));
    const yes = await confirm({
      title: `Post Payroll — ${fmtMonth(month)}`,
      message: `Record ${fmtCurrency(selectedTotal)} for ${entries.length} employee(s) as Salary / Allowances expenses dated ${fmtDate(paymentDate)}?`,
      confirmText: 'Post Payroll',
      variant: 'primary',
    });
    if (!yes) return;
    try {
      await runMutation.mutateAsync(entries);
      showToast(`Payroll for ${fmtMonth(month)} posted to expenses`, 'success');
    } catch (err) {
      showToast(errorMessage(err, 'Failed to post payroll'), 'error');
    }
  }

  async function downloadSlip(r: PayrollRow) {
    setSlipFor(r.employee.id);
    try {
      const blob = await api.download('/employees/payroll/slip', { month, employeeId: r.employee.id });
      triggerBlobDownload(blob, `Salary-Slip_${r.employee.employeeCode}_${month}.pdf`);
    } catch (err) {
      showToast(errorMessage(err, 'Failed to download salary slip'), 'error');
    } finally {
      setSlipFor(null);
    }
  }

  async function undo(r: PayrollRow) {
    const yes = await confirm({
      title: 'Undo Payroll Entry',
      message: `Remove ${r.employee.name}'s ${fmtMonth(month)} salary and allowance expenses (${fmtCurrency(r.salaryPaid + r.allowancesPaid)})?`,
      confirmText: 'Undo',
      variant: 'danger',
    });
    if (!yes) return;
    try {
      await undoMutation.mutateAsync(r.employee.id);
      showToast('Payroll entry reversed', 'success');
    } catch (err) {
      showToast(errorMessage(err, 'Failed to undo'), 'error');
    }
  }

  const cell = 'w-28 px-2 py-1.5 border rounded-md text-sm text-right focus:outline-none focus:ring-2 focus:ring-brand-500';

  return (
    <Layout>
      <div className="max-w-6xl mx-auto">
        <PageHeader
          title="Payroll"
          subtitle="Pay salaries and allowances for a month — each payment is posted to the expense ledger"
          actions={<Button variant="outline" icon="fa-users" onClick={() => navigate('/employees')}>Employees</Button>}
        />

        {/* Month picker */}
        <div className="bg-white border border-ink-200 rounded-xl p-3 mb-4 flex items-center justify-between gap-3">
          <button className="w-10 h-10 rounded-md border border-ink-200 text-ink-600 hover:bg-ink-50" onClick={() => setMonth(shiftMonth(month, -1))} aria-label="Previous month">
            <i className="fas fa-chevron-left text-xs" />
          </button>
          <div className="text-center">
            <p className="text-lg font-extrabold text-ink-900">{fmtMonth(month)}</p>
            <p className="text-xs text-ink-500">{sheet ? `${sheet.totals.paid} of ${sheet.totals.employees} paid` : ' '}</p>
          </div>
          <button className="w-10 h-10 rounded-md border border-ink-200 text-ink-600 hover:bg-ink-50 disabled:opacity-40"
            onClick={() => setMonth(shiftMonth(month, 1))} disabled={month >= currentMonth()} aria-label="Next month">
            <i className="fas fa-chevron-right text-xs" />
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4 lg:mb-6">
          <KpiTile label="Salary Paid" value={fmtCurrency(sheet?.totals.salaryPaid ?? 0, 0)} icon="fa-user-tie" tone="green" />
          <KpiTile label="Allowances Paid" value={fmtCurrency(sheet?.totals.allowancesPaid ?? 0, 0)} icon="fa-hand-holding-dollar" tone="accent" />
          <KpiTile label="To Post Now" value={fmtCurrency(selectedTotal, 0)} icon="fa-hourglass-half" tone="brand" hint={`${selected.length} selected`} />
        </div>

        {isLoading && <Spinner />}
        {!isLoading && rows.length === 0 && (
          <EmptyState icon="fa-users" message={`No employees were on the rolls in ${fmtMonth(month)}.`}
            action={<Button variant="primary" onClick={() => navigate('/employees')}>Add Employees</Button>} />
        )}

        {!isLoading && rows.length > 0 && (
          <>
            <div className="bg-white rounded-xl border border-ink-200 overflow-x-auto mb-4">
              <table className="w-full min-w-[820px] text-sm">
                <thead>
                  <tr className="bg-ink-50 border-b border-ink-200 text-xs font-bold uppercase tracking-wider text-ink-500">
                    <th className="px-3 py-2.5 w-10"></th>
                    <th className="px-3 py-2.5 text-left">Employee</th>
                    <th className="px-3 py-2.5 text-left">Days</th>
                    <th className="px-3 py-2.5 text-right">Salary (₹)</th>
                    <th className="px-3 py-2.5 text-right">Allowances (₹)</th>
                    <th className="px-3 py-2.5 text-left">Remarks</th>
                    <th className="px-3 py-2.5 text-right">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const d = drafts[r.employee.id];
                    const partial = r.workedDays < r.monthDays;
                    const err = rowErrors[r.employee.id] ?? {};
                    return (
                      <tr key={r.employee.id} className="border-b border-ink-100 last:border-0">
                        <td className="px-3 py-2.5 text-center">
                          {!r.isPaid && canRun && d && (
                            <input type="checkbox" checked={d.include} onChange={(e) => patch(r.employee.id, { include: e.target.checked })}
                              className="h-4 w-4 rounded border-ink-300 text-brand-600" aria-label={`Include ${r.employee.name}`} />
                          )}
                        </td>
                        <td className="px-3 py-2.5">
                          <span className="font-semibold text-ink-900">{r.employee.name}</span>
                          <span className="block text-xs text-ink-500">{r.employee.employeeCode}{r.employee.designation ? ` · ${r.employee.designation}` : ''}</span>
                        </td>
                        <td className="px-3 py-2.5 text-ink-600 whitespace-nowrap">
                          {r.workedDays}/{r.monthDays}
                          {partial && <span className="block text-[10px] font-bold uppercase text-accent-600">Prorated</span>}
                        </td>
                        {r.isPaid ? (
                          <>
                            <td className="px-3 py-2.5 text-right font-mono text-ink-800">{fmtCurrency(r.salaryPaid)}</td>
                            <td className="px-3 py-2.5 text-right font-mono text-ink-800">{r.allowancesPaid ? fmtCurrency(r.allowancesPaid) : '—'}</td>
                            <td className="px-3 py-2.5 text-xs text-ink-500">Paid on {fmtDate(r.paymentDate)}</td>
                            <td className="px-3 py-2.5 text-right whitespace-nowrap">
                              <Badge variant="success" size="sm" rounded>Paid</Badge>
                              <button
                                className="ml-2 px-2.5 py-1 bg-brand-600 text-white text-xs rounded hover:bg-brand-700 disabled:opacity-60"
                                title="Download salary slip (PDF)"
                                onClick={() => downloadSlip(r)}
                                disabled={slipFor === r.employee.id}
                              >
                                <i className={`fas ${slipFor === r.employee.id ? 'fa-spinner fa-spin' : 'fa-file-pdf'} mr-1`} />
                                Slip
                              </button>
                              {canRun && (
                                <button className="ml-2 text-xs text-red-600 hover:underline" onClick={() => undo(r)} disabled={undoMutation.isPending}>
                                  Undo
                                </button>
                              )}
                            </td>
                          </>
                        ) : (
                          <>
                            <td className="px-3 py-2.5 text-right align-top">
                              <input className={`${cell} ${err.salary ? 'border-red-500' : 'border-ink-200'}`} inputMode="decimal"
                                aria-label={`Salary for ${r.employee.name}`} aria-invalid={err.salary ? true : undefined}
                                disabled={!canRun || !d?.include} value={d?.salary ?? ''}
                                onChange={(e) => patch(r.employee.id, { salary: moneyInput(e.target.value) })} />
                              {err.salary && <p role="alert" className="mt-1 text-[11px] text-red-600 text-right max-w-[11rem] ml-auto">{err.salary}</p>}
                            </td>
                            <td className="px-3 py-2.5 text-right align-top">
                              <input className={`${cell} ${err.allowances ? 'border-red-500' : 'border-ink-200'}`} inputMode="decimal"
                                aria-label={`Allowances for ${r.employee.name}`} aria-invalid={err.allowances ? true : undefined}
                                disabled={!canRun || !d?.include} value={d?.allowances ?? ''} placeholder="0"
                                onChange={(e) => patch(r.employee.id, { allowances: moneyInput(e.target.value) })} />
                              {err.allowances && <p role="alert" className="mt-1 text-[11px] text-red-600 text-right">{err.allowances}</p>}
                            </td>
                            <td className="px-3 py-2.5 align-top">
                              <input className={`w-full min-w-[140px] px-2 py-1.5 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 ${err.remarks ? 'border-red-500' : 'border-ink-200'}`}
                                aria-label={`Remarks for ${r.employee.name}`}
                                maxLength={255} disabled={!canRun || !d?.include} value={d?.remarks ?? ''} placeholder="Bonus, deduction note…"
                                onChange={(e) => patch(r.employee.id, { remarks: e.target.value })} />
                              {err.remarks && <p role="alert" className="mt-1 text-[11px] text-red-600">{err.remarks}</p>}
                            </td>
                            <td className="px-3 py-2.5 text-right"><Badge variant="secondary" size="sm" rounded>Pending</Badge></td>
                          </>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {canRun && pending.length > 0 && (
              <div className="bg-white border border-ink-200 rounded-xl p-4 flex flex-wrap items-end gap-4">
                <div className="w-48">
                  <Input label="Payment Date" type="date" required value={paymentDate} max={todayISO()} error={formErrors.paymentDate}
                    onChange={(e) => { setPaymentDate(e.target.value); setFormErrors((p) => ({ ...p, paymentDate: undefined })); }} />
                </div>
                <NativeSelect className="w-64" label="Payment Mode" required value={paymentMode} error={formErrors.paymentMode}
                  onChange={(val) => { setPaymentMode(val); setFormErrors((p) => ({ ...p, paymentMode: undefined })); }} options={PAYMENT_MODES} />
                <div className="ml-auto flex items-center gap-4">
                  <p className="text-sm text-ink-600">Total: <span className="text-lg font-extrabold text-ink-900">{fmtCurrency(selectedTotal)}</span></p>
                  <Button variant="primary" icon="fa-check" onClick={run} disabled={runMutation.isPending || selected.length === 0}>
                    {runMutation.isPending ? 'Posting…' : `Post Payroll (${selected.length})`}
                  </Button>
                </div>
              </div>
            )}
          </>
        )}

        <ConfirmDialog />
      </div>
    </Layout>
  );
}
